import { Box, Button, Icon, Image, Link, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { isGachaContentDownloadable } from '~/lib/utils/gacha';
import { type GachaDetail } from '~/types/resources/gachaDetail';

type Props = {
  contentUrl?: string;
  contentDownloadSetting: GachaDetail['content_download'];
};

const formatDate = (date: Date) => {
  return format(date, 'yyyy/MM/dd');
};

export const CollectionItemDownloadContainer: React.FC<Props> = (
  props: Props
) => {
  const isDownloadable = useMemo(() => {
    return isGachaContentDownloadable(props.contentDownloadSetting);
  }, [props.contentDownloadSetting]);
  const isBeforeDownloadableSpan = useMemo(() => {
    if (props.contentDownloadSetting.start_at == null) return false;
    return new Date(props.contentDownloadSetting.start_at) > new Date();
  }, [props.contentDownloadSetting]);
  const isExpiredDownloadableSpan = useMemo(() => {
    if (props.contentDownloadSetting.end_at == null) return false;
    return new Date(props.contentDownloadSetting.end_at) < new Date();
  }, [props.contentDownloadSetting]);
  const downloadableStartDateString = useMemo(() => {
    if (props.contentDownloadSetting.start_at == null) return '';
    return formatDate(new Date(props.contentDownloadSetting.start_at));
  }, [props.contentDownloadSetting]);
  const downloadableDeadlineString = useMemo(() => {
    if (props.contentDownloadSetting.end_at == null) return '';
    return formatDate(new Date(props.contentDownloadSetting.end_at));
  }, [props.contentDownloadSetting]);

  if (!isDownloadable && isBeforeDownloadableSpan) {
    return (
      <Box>
        <DownloadAnnotationText
          text={`※ダウンロードは${downloadableStartDateString}からできます`}
        />
      </Box>
    );
  }

  if (!isDownloadable && isExpiredDownloadableSpan) {
    return (
      <Box textAlign="center">
        <Text>ダウンロード期限が終了しました</Text>
        <Box h={2} />
        <DownloadAnnotationText
          text={`※ダウンロード期限：${downloadableDeadlineString}まで`}
        />
      </Box>
    );
  }

  return (
    <Box textAlign="center">
      <Button
        asChild
        h="32px"
        w="136px"
        px="16px"
        bgColor="white"
        bg="webrollGradient"
        fontSize="13px"
        fontWeight="bold"
        rounded="50px"
        backgroundColor="#1fe0"
        _hover={{ backgroundColor: '#1fe0' }}
        _focus={{ backgroundColor: '#1fe0' }}
        _active={{ backgroundColor: '#1fe0' }}
      >
        <Link href={props.contentUrl} download>
          <Image src="/assets/icon/download.svg" alt="icon" h="13px" w="13px" />
          <Box w="8px" />
          ダウンロード
        </Link>
      </Button>
      <Box h={4} />
      <DownloadAnnotationText
        text={`※ダウンロード期限：${downloadableDeadlineString}まで`}
      />
      <Box py={2}>
        <Link
          href="https://ex.swmp.place/web-roll/howto_download"
          fontSize="xs"
          color="gray.600"
          target="_blank"
          rel="noopener noreferrer"
        >
          ダウンロードできない場合はこちら
          <Icon as={FaExternalLinkAlt} ml={2} w={3} h={3} />
        </Link>
      </Box>
    </Box>
  );
};

const DownloadAnnotationText = (props: { text: string }) => (
  <Text fontSize="sm" color="gray.500">
    {props.text}
  </Text>
);
